// References left menu — folder tree (category → tabs):
//  • drag a reference file from the gallery onto a category/tab to MOVE it
//  • drag categories to reorder; right-click for move/rename/delete/add
//  • right-click empty space to add a category or divider
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../shared/api/client";
import { I } from "../shared/Icon";
import { toast } from "../shared/stores/toasts";
import { useAuth } from "../shared/stores/auth";
import { ContextMenu, type CtxItem } from "./SubNav";
import { IconPicker } from "../shared/components/IconPicker";

type RefCategory = { id: number; label: string; order: number; icon?: string | null; is_trash?: boolean };
type RefTab = { id: number; label: string; order: number; category_id: number };
type RefFilePayload = { category: string; tab: string; name: string };

const ALL = "all";
const DIVIDER = "──────────";           // legacy plain divider (still rendered as a line)
const SECTION_MARK = "❖";               // section divider sentinel: ❖{name}❖{uid}

const isDivider = (c: RefCategory) => c.label.startsWith(SECTION_MARK) || c.label.startsWith(DIVIDER);

/** Parse a section divider's label → { name, uid }. */
function parseSection(label: string): { name: string; uid: string | null } {
  const m = label.match(/^❖([\s\S]*)❖(\d+)$/);
  if (m) return { name: m[1], uid: m[2] };
  if (label.startsWith(SECTION_MARK)) return { name: label.slice(SECTION_MARK.length), uid: null };
  return { name: "", uid: null };        // legacy ────── divider
}
/** Build a unique section label carrying its name. */
function sectionLabel(name: string, uid?: string | null): string {
  return `${SECTION_MARK}${name}${SECTION_MARK}${uid ?? Date.now()}`;
}

const FILE_MIME = "application/x-ref-file";

function readFilePayloads(e: React.DragEvent): RefFilePayload[] {
  const raw = e.dataTransfer.getData(FILE_MIME);
  if (!raw) return [];
  try {
    const v = JSON.parse(raw);
    const arr = Array.isArray(v) ? v : [v];   // accept legacy single-object payloads
    return arr.filter((p) => p && p.category && p.tab && p.name) as RefFilePayload[];
  } catch { return []; }
}

// ── Inline rename input ────────────────────────────────────────────────────────

function RenameInput({ initial, onCommit, onCancel }: {
  initial: string; onCommit: (v: string) => void; onCancel: () => void;
}) {
  const [v, setV] = useState(initial);
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => { ref.current?.focus(); ref.current?.select(); }, []);
  return (
    <input ref={ref} value={v} onChange={e => setV(e.target.value)}
      onClick={e => e.stopPropagation()}
      onKeyDown={e => { if (e.key === "Enter") { e.stopPropagation(); onCommit(v.trim() || initial); } if (e.key === "Escape") { e.stopPropagation(); onCancel(); } }}
      onBlur={() => onCommit(v.trim() || initial)}
      style={{ flex: 1, fontSize: 13, padding: "1px 4px", border: "1px solid var(--p-400,#818cf8)", borderRadius: 3, outline: "none", background: "var(--bg-0)", color: "var(--text-1)", fontFamily: "inherit" }} />
  );
}

// ── Tab leaf row (also a file drop target) ─────────────────────────────────────

function TabRow({ tab, active, isAdmin, onSelect, onRename, onDelete, onFileDrop }: {
  tab: RefTab; active: boolean; isAdmin: boolean;
  onSelect: () => void; onRename: (name: string) => void; onDelete: () => void;
  onFileDrop: (ps: RefFilePayload[]) => void;
}) {
  const [hovered, setHovered] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [fileOver, setFileOver] = useState(false);
  const [ctx, setCtx] = useState<{ x: number; y: number } | null>(null);

  const items: CtxItem[] = isAdmin ? [
    { label: "이름 변경", icon: "✏️", onClick: () => setRenaming(true) },
    { sep: true }, { label: "삭제", icon: "🗑️", onClick: onDelete, danger: true },
  ] : [];

  return (
    <div onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      onContextMenu={e => { if (isAdmin) { e.preventDefault(); e.stopPropagation(); setCtx({ x: e.clientX, y: e.clientY }); } }}
      onDragOver={e => { if (e.dataTransfer.types.includes(FILE_MIME)) { e.preventDefault(); e.dataTransfer.dropEffect = "move"; setFileOver(true); } }}
      onDragLeave={() => setFileOver(false)}
      onDrop={e => { const ps = readFilePayloads(e); setFileOver(false); if (ps.length) { e.preventDefault(); e.stopPropagation(); onFileDrop(ps); } }}
      style={{ display: "flex", alignItems: "center", margin: "1px 6px 1px 0", paddingLeft: 30, borderRadius: 6,
        background: fileOver ? "rgba(99,102,241,0.22)" : active ? "rgba(99,102,241,0.12)" : hovered ? "rgba(99,102,241,0.05)" : "transparent",
        outline: fileOver ? "1.5px dashed var(--p-500,#6366f1)" : "none" }}>
      <button onClick={onSelect} style={{ flex: 1, display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", padding: "5px 4px", textAlign: "left", minWidth: 0 }}>
        <span style={{ fontSize: 11, color: "var(--text-3)", flexShrink: 0 }}>＃</span>
        {renaming ? (
          <RenameInput initial={tab.label} onCommit={n => { setRenaming(false); if (n !== tab.label) onRename(n); }} onCancel={() => setRenaming(false)} />
        ) : (
          <span style={{ flex: 1, fontSize: 12.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: active ? "var(--p-600,#4f46e5)" : "var(--text-2)", fontWeight: active ? 600 : 400 }}>{tab.label}</span>
        )}
      </button>
      {ctx && <ContextMenu x={ctx.x} y={ctx.y} items={items} onClose={() => setCtx(null)} />}
    </div>
  );
}

// ── Category row (drop target for files + category reorder) ─────────────────────

function CategoryRow({
  cat, active, isAdmin, expanded, activeTabLabel, tabs, highlight,
  onToggle, onSelect, onRename, onDelete, onAddTab, onAddDivider, onMoveUp, onMoveDown, onSetIcon,
  onTabSelect, onTabRename, onTabDelete, onTabFileDrop,
  onDragStart, onDragOver, onDrop, onDragEnd,
}: {
  cat: RefCategory; active: boolean; isAdmin: boolean; expanded: boolean;
  activeTabLabel: string | null; tabs: RefTab[]; highlight: boolean;
  onToggle: () => void; onSelect: () => void; onRename: (name: string) => void;
  onDelete: () => void; onAddTab: () => void; onAddDivider: () => void;
  onMoveUp: () => void; onMoveDown: () => void; onSetIcon: (icon: string) => void;
  onTabSelect: (t: RefTab) => void; onTabRename: (t: RefTab, name: string) => void; onTabDelete: (t: RefTab) => void;
  onTabFileDrop: (t: RefTab, ps: RefFilePayload[]) => void;
  onDragStart: () => void; onDragOver: () => void; onDrop: (e: React.DragEvent) => void; onDragEnd: () => void;
}) {
  const [renaming, setRenaming] = useState(false);
  const [pickingIcon, setPickingIcon] = useState(false);
  const [ctx, setCtx] = useState<{ x: number; y: number } | null>(null);

  if (isDivider(cat)) {
    const sec = parseSection(cat.label);
    return (
      <div onContextMenu={e => { if (isAdmin) { e.preventDefault(); e.stopPropagation(); setCtx({ x: e.clientX, y: e.clientY }); } }}
        draggable={isAdmin && !renaming}
        onDragStart={e => { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", String(cat.id)); onDragStart(); }}
        onDragOver={e => { e.preventDefault(); onDragOver(); }} onDrop={e => { e.preventDefault(); onDrop(e); }} onDragEnd={onDragEnd}
        onDoubleClick={() => { if (isAdmin) setRenaming(true); }}
        title={isAdmin ? "더블클릭 또는 우클릭으로 섹션 이름 변경" : undefined}
        style={{ padding: "9px 12px 4px", cursor: isAdmin ? "grab" : "default" }}>
        {renaming ? (
          <RenameInput initial={sec.name} onCommit={n => { setRenaming(false); onRename(n); }} onCancel={() => setRenaming(false)} />
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {sec.name && <span style={{ fontSize: 10.5, fontWeight: 700, color: "var(--text-3)", whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: ".04em", flexShrink: 0 }}>{sec.name}</span>}
            <div style={{ flex: 1, height: 1, background: highlight ? "var(--p-500,#6366f1)" : "var(--border)" }} />
          </div>
        )}
        {ctx && <ContextMenu x={ctx.x} y={ctx.y} onClose={() => setCtx(null)} items={[
          { label: "이름 변경", icon: "✏️", onClick: () => setRenaming(true) },
          { sep: true },
          { label: "삭제", icon: "🗑️", onClick: onDelete, danger: true },
        ]} />}
      </div>
    );
  }

  const items: CtxItem[] = isAdmin ? [
    { label: "탭 추가", icon: "＃", onClick: onAddTab },
    { label: "이름 변경", icon: "✏️", onClick: () => setRenaming(true) },
    { label: "아이콘 변경", icon: "🎨", onClick: () => setPickingIcon(true) },
    { sep: true },
    { label: "위로 이동", icon: "↑", onClick: onMoveUp },
    { label: "아래로 이동", icon: "↓", onClick: onMoveDown },
    { sep: true },
    { label: "섹션 추가", icon: "―", onClick: onAddDivider },
    { sep: true },
    { label: "삭제", icon: "🗑️", onClick: onDelete, danger: true },
  ] : [];

  return (
    <div>
      <div
        draggable={isAdmin && !renaming}
        onDragStart={e => { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", String(cat.id)); onDragStart(); }}
        onDragOver={e => { e.preventDefault(); onDragOver(); }}
        onDrop={e => { e.preventDefault(); onDrop(e); }}
        onDragEnd={onDragEnd}
        onContextMenu={e => { if (isAdmin) { e.preventDefault(); e.stopPropagation(); setCtx({ x: e.clientX, y: e.clientY }); } }}
        style={{ display: "flex", alignItems: "center", margin: "1px 6px 1px 0", borderRadius: 6,
          background: highlight ? "rgba(99,102,241,0.2)" : active ? "rgba(99,102,241,0.12)" : "transparent",
          outline: highlight ? "1.5px dashed var(--p-500,#6366f1)" : "none",
          cursor: isAdmin && !renaming ? "grab" : "pointer" }}
        onMouseEnter={e => { if (!active && !highlight) (e.currentTarget as HTMLElement).style.background = "rgba(99,102,241,0.05)"; }}
        onMouseLeave={e => { if (!active && !highlight) (e.currentTarget as HTMLElement).style.background = "transparent"; }}>
        <button onClick={e => { e.stopPropagation(); onToggle(); }}
          style={{ background: "none", border: "none", cursor: "pointer", padding: "4px 2px", color: "var(--text-3)", flexShrink: 0, display: "flex", alignItems: "center" }}>
          {tabs.length > 0 ? (expanded ? <I.ChevD size={11} /> : <I.ChevR size={11} />) : <span style={{ width: 11, display: "inline-block" }} />}
        </button>
        <button onClick={onSelect} style={{ flex: 1, display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", padding: "5px 4px", textAlign: "left", minWidth: 0 }}>
          <span style={{ fontSize: 13, flexShrink: 0 }}>{cat.icon || "📁"}</span>
          {renaming ? (
            <RenameInput initial={cat.label} onCommit={n => { setRenaming(false); if (n !== cat.label) onRename(n); }} onCancel={() => setRenaming(false)} />
          ) : (
            <span style={{ flex: 1, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: active ? "var(--p-600,#4f46e5)" : "var(--text-2)", fontWeight: active ? 600 : 400 }}>{cat.label}</span>
          )}
        </button>
      </div>

      {pickingIcon && (
        <div className="t-overlay" onClick={() => setPickingIcon(false)}>
          <div className="t-cmdk" onClick={e => e.stopPropagation()} style={{ width: 360, maxWidth: "92vw", padding: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <span style={{ fontSize: 22 }}>{cat.icon || "📁"}</span>
              <h3 style={{ margin: 0, fontSize: 14, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>아이콘 변경 — {cat.label}</h3>
              <button onClick={() => setPickingIcon(false)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, color: "var(--text-3)" }}>✕</button>
            </div>
            <IconPicker value={cat.icon || "📁"} onChange={(ic) => { onSetIcon(ic); setPickingIcon(false); }} />
          </div>
        </div>
      )}

      {expanded && tabs.map(t => (
        <TabRow key={t.id} tab={t} active={active && activeTabLabel === t.label} isAdmin={isAdmin}
          onSelect={() => onTabSelect(t)} onRename={n => onTabRename(t, n)} onDelete={() => onTabDelete(t)}
          onFileDrop={ps => onTabFileDrop(t, ps)} />
      ))}

      {ctx && <ContextMenu x={ctx.x} y={ctx.y} items={items} onClose={() => setCtx(null)} />}
    </div>
  );
}

// ── Main sidebar ────────────────────────────────────────────────────────────────

export function ReferencesSidebar({ onCollapse }: { onCollapse: () => void }) {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const activeCatId = params.get("filter") || ALL;
  const activeTabLabel = params.get("tab");

  const canManage = useAuth(s => s.canManageMenu("references") || s.hasRole("SUPER_ADMIN"));

  const [cats, setCats] = useState<RefCategory[]>([]);
  const [tabsByCat, setTabsByCat] = useState<Record<number, RefTab[]>>({});
  // Accordion: only one category expanded at a time.
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const newRef = useRef<HTMLInputElement>(null);

  const dragId = useRef<number | null>(null);
  const [dropId, setDropId] = useState<number | null>(null);
  const [bgCtx, setBgCtx] = useState<{ x: number; y: number } | null>(null);

  const loadCats = () => api.get<RefCategory[]>("/references/categories").then(r => setCats(r.data)).catch(() => {});
  const loadTabs = (catId: number) =>
    api.get<RefTab[]>(`/references/tabs?category_id=${catId}`).then(r => { setTabsByCat(p => ({ ...p, [catId]: r.data })); return r.data; }).catch(() => [] as RefTab[]);

  useEffect(() => {
    loadCats();
    const h = () => loadCats();
    window.addEventListener("reference-categories-updated", h);
    return () => window.removeEventListener("reference-categories-updated", h);
  }, []);

  // Clear drop highlight whenever any drag ends (incl. file drags from the gallery).
  useEffect(() => {
    const clear = () => setDropId(null);
    document.addEventListener("dragend", clear);
    document.addEventListener("drop", clear);
    return () => { document.removeEventListener("dragend", clear); document.removeEventListener("drop", clear); };
  }, []);

  useEffect(() => {
    if (activeCatId !== ALL) {
      const id = Number(activeCatId);
      setExpandedId(id);   // accordion — opening the active one closes others
      if (!tabsByCat[id]) loadTabs(id);
    }
  }, [activeCatId]);

  const toggle = (catId: number) => {
    setExpandedId(prev => (prev === catId ? null : catId));
    if (!tabsByCat[catId]) loadTabs(catId);
  };

  const goCat = (id: string) => nav(id === ALL ? "/references" : `/references?filter=${id}`);
  const goTab = (catId: number, label: string) => nav(`/references?filter=${catId}&tab=${encodeURIComponent(label)}`);
  const notify = () => window.dispatchEvent(new Event("reference-categories-updated"));

  // ── Category CRUD ──────────────────────────────────────────────────────────
  const addCategory = async (label: string) => {
    const v = label.trim(); if (!v) return;
    try { await api.post("/references/categories", { label: v }); loadCats(); notify(); }
    catch (e: any) {
      const msg = e?.response?.status === 409 ? "이미 같은 이름의 카테고리가 있습니다" : (e?.response?.data?.detail ?? "");
      toast.error("추가 실패", msg);
    }
  };
  const addSection = async () => {
    const raw = prompt("섹션 이름 (비워두면 선만 표시)");
    if (raw === null) return;   // cancelled
    try { await api.post("/references/categories", { label: sectionLabel(raw.trim()) }); loadCats(); notify(); }
    catch { toast.error("섹션 추가 실패"); }
  };
  const renameCat = async (c: RefCategory, value: string) => {
    // For section dividers, `value` is the section NAME (wrap with marker + uid).
    const label = isDivider(c) ? sectionLabel(value, parseSection(c.label).uid) : value;
    try { await api.patch(`/references/categories/${c.id}`, { label }); loadCats(); notify(); } catch { toast.error("이름 변경 실패"); }
  };
  const setCatIcon = async (c: RefCategory, icon: string) => {
    try { await api.patch(`/references/categories/${c.id}/icon`, { icon }); loadCats(); notify(); } catch { toast.error("아이콘 변경 실패"); }
  };
  const deleteCat = async (c: RefCategory) => {
    if (!isDivider(c) && !confirm(`"${c.label}" 카테고리와 그 안의 모든 탭·파일을 삭제하시겠습니까?`)) return;
    try { await api.delete(`/references/categories/${c.id}`); loadCats(); notify(); if (activeCatId === String(c.id)) nav("/references"); }
    catch { toast.error("삭제 실패"); }
  };
  const reorderCats = async (ids: number[]) => {
    setCats(ids.map(id => cats.find(c => c.id === id)!).filter(Boolean));   // optimistic
    try { await api.put("/references/categories/reorder", { ids }); notify(); } catch { toast.error("순서 변경 실패"); loadCats(); }
  };
  const moveCatByOne = (c: RefCategory, dir: -1 | 1) => {
    const ids = cats.map(x => x.id);
    const i = ids.indexOf(c.id), j = i + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    reorderCats(ids);
  };

  // ── Tab CRUD ────────────────────────────────────────────────────────────────
  const addTab = async (catId: number) => {
    const label = prompt("새 탭 이름")?.trim();
    if (!label) return;
    try { await api.post("/references/tabs", { category_id: catId, label }); loadTabs(catId); setExpandedId(catId); notify(); }
    catch (e: any) { toast.error("탭 추가 실패", e?.response?.data?.detail ?? ""); }
  };
  const renameTab = async (catId: number, t: RefTab, label: string) => {
    try { await api.patch(`/references/tabs/${t.id}`, { label }); loadTabs(catId); notify(); } catch { toast.error("이름 변경 실패"); }
  };
  const deleteTab = async (catId: number, t: RefTab) => {
    if (!confirm(`"${t.label}" 탭과 그 안의 파일을 삭제하시겠습니까?`)) return;
    try { await api.delete(`/references/tabs/${t.id}`); loadTabs(catId); notify(); } catch { toast.error("삭제 실패"); }
  };

  // ── Move dragged file(s) into a category (→ first tab) or a specific tab ──────
  const moveFiles = async (ps: RefFilePayload[], newCat: string, newTab: string) => {
    const targets = ps.filter(p => !(p.category === newCat && p.tab === newTab));
    if (!targets.length) return;
    let ok = 0;
    for (const p of targets) {
      try {
        await api.patch("/references/files/move", { category: p.category, tab: p.tab, name: p.name, new_category: newCat, new_tab: newTab });
        ok++;
      } catch (e: any) { toast.error("이동 실패", `${p.name}: ${e?.response?.data?.detail ?? ""}`); }
    }
    if (ok) {
      toast.success(`${ok}개 이동됨`, `${newCat} · ${newTab}`);
      window.dispatchEvent(new Event("reference-files-updated"));
    }
  };
  const dropFilesOnCategory = async (cat: RefCategory, ps: RefFilePayload[]) => {
    let tabs = tabsByCat[cat.id];
    if (!tabs) tabs = await loadTabs(cat.id);
    const first = tabs?.[0];
    if (!first) { toast.error("탭이 없습니다", "이 카테고리에 탭을 먼저 만드세요"); return; }
    await moveFiles(ps, cat.label, first.label);
  };

  // ── Drop on a category: file move OR category reorder ────────────────────────
  const handleCatDrop = async (e: React.DragEvent, cat: RefCategory) => {
    const ps = readFilePayloads(e);
    setDropId(null);
    if (ps.length) { await dropFilesOnCategory(cat, ps); return; }
    const from = dragId.current; dragId.current = null;
    if (from == null || from === cat.id) return;
    const ids = cats.map(c => c.id);
    const fromIdx = ids.indexOf(from), toIdx = ids.indexOf(cat.id);
    if (fromIdx < 0 || toIdx < 0) return;
    ids.splice(toIdx, 0, ids.splice(fromIdx, 1)[0]);
    reorderCats(ids);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      <div style={{ padding: "14px 12px 10px", borderBottom: "1px solid var(--border)", flexShrink: 0, display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "var(--text-1)" }}>레퍼런스</h3>
          <p style={{ margin: "2px 0 0", fontSize: 11, color: "var(--text-3)" }}>이미지 · 자료</p>
        </div>
        <button onClick={onCollapse} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-3)", padding: 2, borderRadius: 4 }} title="접기"><I.PanelL size={14} /></button>
      </div>

      <div style={{ padding: "6px 8px 2px", flexShrink: 0 }}>
        <button onClick={() => goCat(ALL)}
          style={{ display: "flex", alignItems: "center", gap: 7, width: "100%", border: "none", cursor: "pointer", textAlign: "left", padding: "6px 10px", borderRadius: 6, fontSize: 13,
            background: activeCatId === ALL ? "rgba(99,102,241,0.12)" : "transparent", color: activeCatId === ALL ? "var(--p-600,#4f46e5)" : "var(--text-2)", fontWeight: activeCatId === ALL ? 600 : 400 }}>
          <span>📊</span><span>대시보드</span>
        </button>
      </div>

      {/* Tree — right-click empty space to add category/divider */}
      <div style={{ flex: 1, overflowY: "auto", padding: "2px 4px" }}
        onContextMenu={e => { if (canManage) { e.preventDefault(); setBgCtx({ x: e.clientX, y: e.clientY }); } }}>
        <div style={{ display: "flex", alignItems: "center", padding: "6px 8px 2px 10px" }}>
          <span style={{ flex: 1, fontSize: 10, fontWeight: 700, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: "0.06em" }}>카테고리</span>
          {canManage && (
            <button title="카테고리 추가" onClick={() => { setAdding(true); setTimeout(() => newRef.current?.focus(), 0); }}
              style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-3)", padding: "2px 4px", borderRadius: 3, display: "flex", alignItems: "center" }}><I.Plus size={12} /></button>
          )}
        </div>

        {cats.map(c => (
          <CategoryRow key={c.id} cat={c} isAdmin={!!canManage}
            active={activeCatId === String(c.id)} expanded={expandedId === c.id}
            activeTabLabel={activeTabLabel} tabs={tabsByCat[c.id] ?? []}
            highlight={dropId === c.id}
            onToggle={() => toggle(c.id)} onSelect={() => goCat(String(c.id))}
            onRename={n => renameCat(c, n)} onDelete={() => deleteCat(c)}
            onAddTab={() => addTab(c.id)} onAddDivider={addSection}
            onMoveUp={() => moveCatByOne(c, -1)} onMoveDown={() => moveCatByOne(c, 1)}
            onSetIcon={(ic) => setCatIcon(c, ic)}
            onTabSelect={t => goTab(c.id, t.label)} onTabRename={(t, n) => renameTab(c.id, t, n)} onTabDelete={t => deleteTab(c.id, t)}
            onTabFileDrop={(t, ps) => moveFiles(ps, c.label, t.label)}
            onDragStart={() => { dragId.current = c.id; }}
            onDragOver={() => setDropId(c.id)}
            onDrop={(e) => handleCatDrop(e, c)}
            onDragEnd={() => { dragId.current = null; setDropId(null); }} />
        ))}

        {adding && (
          <div style={{ display: "flex", gap: 4, margin: "2px 6px", padding: "4px 8px", alignItems: "center", background: "rgba(99,102,241,0.07)", borderRadius: 6, border: "1px dashed var(--p-400,#818cf8)" }}>
            <span style={{ fontSize: 13 }}>📁</span>
            <input ref={newRef} value={newLabel} placeholder="카테고리 이름…" onChange={e => setNewLabel(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") { addCategory(newLabel); setNewLabel(""); setAdding(false); } if (e.key === "Escape") { setAdding(false); setNewLabel(""); } }}
              onBlur={() => { if (newLabel.trim()) addCategory(newLabel); setNewLabel(""); setAdding(false); }}
              style={{ flex: 1, fontSize: 12, border: "none", outline: "none", background: "transparent", color: "var(--text-1)", fontFamily: "inherit" }} />
          </div>
        )}
      </div>

      {bgCtx && (
        <ContextMenu x={bgCtx.x} y={bgCtx.y} onClose={() => setBgCtx(null)} items={[
          { label: "카테고리 추가", icon: "📁", onClick: () => { setAdding(true); setTimeout(() => newRef.current?.focus(), 0); } },
          { label: "섹션 추가", icon: "―", onClick: addSection },
        ]} />
      )}
    </div>
  );
}
